import { createContext, useState } from 'react'
import { node } from 'prop-types'
import getConfig from 'next/config'

const { publicRuntimeConfig } = getConfig()
const env = publicRuntimeConfig.emarsysEnv || 'TEST'

export const EmarsysWebExtend = createContext({
    pushUser: (email) => {},
    pushItemView: (productCode, email) => {},
    pushSearch: (term, email) => {},
    pushCategoryView: (categoryId, email) => {},
    pushCartActivity: (items, email) => {},
    clearCart: (email) => {},
    trackPurchase: (entries, orderNumber, email) => {},
    trackPageVisit: (url, title, cttag, location, language, email) => {}
})

export const EmarsysWebExtendProvider = ({ children }) => {

    const pushUser = (email) => {
        if(window.ScarabQueue){
            if (env === 'TEST'){
                window.ScarabQueue.push(['testMode'])
            }
            window.ScarabQueue.push(['setEmail', email])
            window.ScarabQueue.push(['go'])
        }
    }

    const pushItemView = (productCode, email) => {
        if(window.ScarabQueue){
            if (env === 'TEST'){
                window.ScarabQueue.push(['testMode'])
            }
            if(email){
                window.ScarabQueue.push(['setEmail', email])
            }
            window.ScarabQueue.push(["view", productCode])
            window.ScarabQueue.push(['go'])
        }
    }

    const pushCategoryView = (categoryId, categoryName, email) => {
        if(window.ScarabQueue){
            if (env === 'TEST'){
                window.ScarabQueue.push(['testMode'])
            }
            const categoryTree = categoryId.toUpperCase().split('-')
            let emarsysCat = categoryTree[0]
            if (categoryTree.length > 1){
                emarsysCat = emarsysCat + ">" + getNameWithoutSeason(categoryTree[1].substring(0, 5))
            }
            if (categoryTree.length > 2){
                emarsysCat = emarsysCat + ">" + categoryName
            }
            if(email){
                window.ScarabQueue.push(['setEmail', email])
            }
            window.ScarabQueue.push(["category", emarsysCat])
            window.ScarabQueue.push(['go'])
        }
    }

    const pushSearch = (term, email) => {
        if(window.ScarabQueue){
            if (env === 'TEST'){
                window.ScarabQueue.push(['testMode'])
            }
            if(email){
                window.ScarabQueue.push(['setEmail', email])
            }
            window.ScarabQueue.push(["searchTerm", term])
            window.ScarabQueue.push(['go'])
        }
    }

    const pushCartActivity = (entries, email) => {
        if(window.ScarabQueue){  
            let cart = []
            entries.forEach(entry => {
                const itemId = entry.items[0].itemId.code?.split('-')?.[0]
                const price = getPrice(entry.items[0].purchasePrice)
                const quantity = entry.quantity

                const existingItem = cart.find(cartItem => cartItem.item === itemId)

                if (existingItem) {
                    // If item already exists, increase the quantity
                    existingItem.quantity += quantity
                } else {
                    // If item doesn't exist, add a new entry
                    cart.push({
                        item: itemId,
                        price: price,
                        quantity: quantity
                    })
                }
            })
            if (env === 'TEST'){
                window.ScarabQueue.push(['testMode'])
            }
            if(email){
                window.ScarabQueue.push(['setEmail', email])
            }
            window.ScarabQueue.push(["cart", cart])
            window.ScarabQueue.push(['go'])
        }
    }

    const clearCart = (email) => {
        if(window.ScarabQueue){  
            if (env === 'TEST'){
                window.ScarabQueue.push(['testMode'])
            }
            if(email){
                window.ScarabQueue.push(['setEmail', email])
            }
            window.ScarabQueue.push(["cart", []])
            window.ScarabQueue.push(['go'])
        }
    }

    const trackPurchase = (entries, orderNumber, email) => {
        if(window.ScarabQueue){  
            let cart = []
            entries.forEach(entry => {
                const itemId = entry.items[0].itemId.code?.split('-')?.[0]
                const price = getPrice(entry.items[0].purchasePrice)
                const quantity = entry.quantity

                const existingItem = cart.find(cartItem => cartItem.item === itemId)

                if (existingItem) {
                    // If item already exists, increase the quantity
                    existingItem.quantity += quantity
                } else {
                    // If item doesn't exist, add a new entry
                    cart.push({
                        item: itemId,
                        price: price,
                        quantity: quantity
                    })
                }
            })
            if (env === 'TEST'){
                window.ScarabQueue.push(['testMode'])
            }
            if(email){
                window.ScarabQueue.push(['setEmail', email])
            }
            window.ScarabQueue.push(["purchase", {"orderId": orderNumber, "items": cart}])
            window.ScarabQueue.push(["cart", []])
            window.ScarabQueue.push(['go'])
        }
    }

    const trackPageVisit = (url, title, cttag, location, language, email) => {
        if(window.ScarabQueue){
            if (env === 'TEST'){
                window.ScarabQueue.push(['testMode'])
            }
            if(email){
                window.ScarabQueue.push(['setEmail', email])
            }
            window.ScarabQueue.push(['tag', 'content_pageview', {
                ...(cttag ? { content_category: cttag } : {}),
                content_url: url,
                content_title: title,
                shippingLocation: location,
                language
              }])
        }
    }

    const getPrice = (price) => {
        const exchangeRates = {
            USD: 0.8857,
            GBP: 1.1730,
            RUB: 0.0110,
          }
          const rate = exchangeRates[price.moneyAmounts[0].currency] || 1
          return parseFloat((price.moneyAmounts[0].amount * rate).toFixed(2))
    }

    const getNameWithoutSeason = (name) => {
        switch (name) {
            case "BORSE":
                return "BORSE"
            case "ACCES":
                return "ACCESSORI"
            case "GIACC":
                return "GIACCHE"
            case "SCARP":
                return "SCARPE"
            default:
                return name
        }
    }

    return (
        <EmarsysWebExtend.Provider value={{
            pushUser,
            pushItemView,
            pushCategoryView,
            pushSearch,
            pushCartActivity,
            clearCart,
            trackPurchase,
            trackPageVisit
        }}
        >
            {children}
        </EmarsysWebExtend.Provider>
    )
}

EmarsysWebExtendProvider.propTypes = {
    children: node.isRequired
}